import { useCallback } from 'react';
import { useQuery } from 'urql';
import {
  GetShapesForProjectDeepDocument,
  GetSheetShapesForProjectDeepDocument,
  ShapeDeepFragment,
  SheetShapeDeepFragment,
} from '../gql/graphql';
import {
  findNextAvailableName,
  findNextAvailableNameForCopy,
} from '../utils/naming-utils';
import { ShapeDrawingResultType } from '../components/orderContractor/shapes/ShapeDrawing';
import { useUserTenant } from 'src/services/auth-info';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';

type UseShapesReturn = {
  getNewShapeName: (
    tenant: string,
    drawingResultType: ShapeDrawingResultType
  ) => string;
  getDuplicatedShapeName: (nameToCopy: string) => string;
};

// Hook for functionality that is shared between bim shapes and sheet shapes
export const useShapes = (projectId: string): UseShapesReturn => {
  const [{ data: shapesData, error: shapesError, fetching: shapesFetching }] =
    useQuery({
      query: GetShapesForProjectDeepDocument,
      variables: { projectId },
    });

  const [
    {
      data: sheetShapesData,
      error: sheetShapesError,
      fetching: sheetShapesFetching,
    },
  ] = useQuery({
    query: GetSheetShapesForProjectDeepDocument,
    variables: { projectId },
  });

  const { isProtan } = useUserTenant();
  const { viewerMode } = useViewerMode();

  const getNewShapeName = useCallback(
    (tenant: string, drawingResultType: ShapeDrawingResultType) => {
      const existingNames = [
        ...(shapesData?.project?.shapes ?? []).map((shape) => shape.name),
        ...(sheetShapesData?.project?.sheetShapes ?? []).map(
          (shape) => shape.name
        ),
      ];

      // Question: Can we rename these to English?
      if (isProtan) {
        if (drawingResultType === ShapeDrawingResultType.Polygon) {
          return viewerMode === ViewerMode.SheetsV2
            ? 'Roof Outline'
            : findNextAvailableName('Zone', existingNames);
        } else if (drawingResultType === ShapeDrawingResultType.Point) {
          return 'Drain';
        } else if (drawingResultType === ShapeDrawingResultType.Wedge) {
          return 'Wedge';
        } else if (drawingResultType === ShapeDrawingResultType.DrainLine) {
          return 'Tapered Insulation';
        }
      }
      return findNextAvailableName('Shape', existingNames);
    },
    [
      isProtan,
      shapesData?.project?.shapes,
      sheetShapesData?.project?.sheetShapes,
      viewerMode,
    ]
  );

  const getDuplicatedShapeName = useCallback(
    (nameToCopy: string) => {
      const existingNames = [
        ...(shapesData?.project?.shapes ?? []).map((shape) => shape.name),
        ...(sheetShapesData?.project?.sheetShapes ?? []).map(
          (shape) => shape.name
        ),
      ];

      return findNextAvailableNameForCopy(nameToCopy, existingNames);
    },
    [shapesData?.project?.shapes, sheetShapesData?.project?.sheetShapes]
  );

  return {
    getNewShapeName,
    getDuplicatedShapeName,
  };
};

export const getShapeType = (
  shape: SheetShapeDeepFragment | ShapeDeepFragment
) => {
  if (shape.__typename === 'SheetShape') {
    const sheetShape = shape as SheetShapeDeepFragment;
    if (sheetShape.sheetShapePolygon) {
      return 'Polygon';
    } else if (sheetShape.sheetShapeLine) {
      return 'Line';
    } else if (sheetShape.sheetShapePoint) {
      return 'Point';
    }
  }
  const bimShape = shape as ShapeDeepFragment;
  if (bimShape.polygon) {
    return 'Polygon';
  } else if (bimShape.line) {
    return 'Line';
  } else if (bimShape.shapePoint) {
    return 'Point';
  }
};
